"use client";

import { Compass, Box, Leaf, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    index: "§ 02.1",
    icon: Compass,
    title: "Practical",
    italicWord: "Life",
    titleSuffix: "",
    description: "Developing independence through purposeful movement, self-care, and the mastery of daily tasks that build confidence and coordination."
  }, {
    index: "§ 02.2",
    icon: Box,
    title: "Sensorial",
    italicWord: "Exploration",
    titleSuffix: "",
    description: "Refining the senses through tactile materials that isolate qualities like size, color, and texture to build a foundation for abstract thought."
  }, {
    index: "§ 02.3",
    icon: Leaf,
    title: "Natural",
    italicWord: "Discovery",
    titleSuffix: "",
    description: "Connecting with the living world through outdoor play, gardening, and observation, fostering a deep respect for our environment."
  }];
  return <section data-section-id="2110" id="nourishing-young-minds" className="relative overflow-hidden bg-[hsl(145_30%_12%)] py-28 md:py-36 dark" style={{
    color: "hsl(40 33% 96%)"
  }}>
      {}
      <div aria-hidden="true" className="pointer-events-none absolute right-[-4%] top-[2%] hidden select-none md:block" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "clamp(320px, 52vw, 760px)",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(40 33% 96%)",
      opacity: 0.06,
      fontWeight: 350
    }}>
        02
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-[-8%] top-[1%] block select-none md:hidden" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "320px",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(40 33% 96%)",
      opacity: 0.06,
      fontWeight: 350
    }}>
        02
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            {}
            <div className="flex items-center gap-2 uppercase" style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.15em",
            lineHeight: 1
          }}>
              <span style={{
              color: "hsl(45 90% 45%)"
            }}>§ 02</span>
              <span style={{
              color: "hsl(145 20% 24%)"
            }}>—</span>
              <span style={{
              color: "hsl(145 10% 75%)"
            }}>OUR APPROACH / MONTESSORI METHOD</span>
            </div>

            {}
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1" style={{
              backgroundColor: "hsl(145 20% 24%)"
            }} />
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "hsl(45 90% 45%)"
              }} />
                <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "hsl(45 90% 45%)"
              }} />
                <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "hsl(45 90% 45%)"
              }} />
              </div>
            </div>

            {}
            <h2 className="mt-8 font-default" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            color: "hsl(40 33% 96%)"
          }}>
              Nurturing potential,{" "}
              <br className="hidden sm:block" />
              one{" "}
              <em style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500
            }}>
                acorn
              </em>
              .
            </h2>
          </div>

          <div className="lg:col-span-5 lg:flex lg:items-end">
            <p className="font-default max-w-md" style={{
            fontSize: "clamp(1.125rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.6,
            color: "hsl(145 10% 75%)"
          }}>
              At Little Acorns, we cultivate a prepared environment where children ages 2.5 to 6 discover their unique capabilities through the timeless Montessori philosophy.
            </p>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="mt-20 grid grid-cols-1 gap-6 md:gap-7 lg:grid-cols-12 lg:gap-6">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={i} data-index={i} className={`lg:col-span-4 transition-all ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDuration: "720ms",
            transitionDelay: `${i * 80}ms`
          }}>
                <Card className="group relative h-full overflow-hidden p-9 transition-transform duration-300 ease-out hover:-translate-y-1" style={{
              backgroundColor: "hsl(145 30% 12%)",
              color: "hsl(40 33% 96%)",
              borderRadius: "1rem",
              border: "1px solid hsl(145 20% 24%)"
            }}>
                  {}
                  <span aria-hidden="true" className="pointer-events-none absolute left-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderTop: "1px solid hsl(45 90% 45%)",
                borderLeft: "1px solid hsl(45 90% 45%)"
              }} />
                  <span aria-hidden="true" className="pointer-events-none absolute right-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderTop: "1px solid hsl(45 90% 45%)",
                borderRight: "1px solid hsl(45 90% 45%)"
              }} />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 left-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderBottom: "1px solid hsl(45 90% 45%)",
                borderLeft: "1px solid hsl(45 90% 45%)"
              }} />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderBottom: "1px solid hsl(45 90% 45%)",
                borderRight: "1px solid hsl(45 90% 45%)"
              }} />

                  {}
                  <div className="flex items-center gap-2 uppercase" style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.15em",
                lineHeight: 1
              }}>
                    <span style={{
                  color: "hsl(45 90% 45%)"
                }}>{pillar.index}</span>
                    <span style={{
                  color: "hsl(145 20% 24%)"
                }}>—</span>
                    <span style={{
                  color: "hsl(145 10% 75%)"
                }}>FOCUS</span>
                  </div>

                  {}
                  <div className="mt-8 transition-transform duration-[400ms] ease-out group-hover:rotate-6">
                    <Icon size={32} strokeWidth={1.5} style={{
                  color: "hsl(45 90% 45%)"
                }} />
                  </div>

                  {}
                  <h3 className="mt-7 font-default" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "0",
                lineHeight: 1.3,
                color: "hsl(40 33% 96%)"
              }}>
                    {pillar.title}{" "}
                    <em style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontWeight: 500
                }}>
                      {pillar.italicWord}
                    </em>
                  </h3>

                  {}
                  <p className="font-default mt-5" style={{
                fontSize: "1rem",
                fontWeight: 400,
                letterSpacing: "0",
                lineHeight: 1.5,
                color: "hsl(145 10% 75%)"
              }}>
                    {pillar.description}
                  </p>

                  {}
                  <div className="mt-9 flex items-center gap-3">
                    <div className="h-px flex-1" style={{
                  backgroundColor: "hsl(145 20% 24%)"
                }} />
                    <div className="flex items-center gap-1">
                      <span className="h-1 w-1 rounded-lg" style={{
                    backgroundColor: "hsl(45 90% 45%)"
                  }} />
                      <span className="h-1 w-1 rounded-lg" style={{
                    backgroundColor: "hsl(45 90% 45%)"
                  }} />
                      <span className="h-1 w-1 rounded-lg" style={{
                    backgroundColor: "hsl(45 90% 45%)"
                  }} />
                    </div>
                  </div>

                  {}
                  <div className="mt-6">
                    <button type="button" className="group/link relative inline-flex items-center gap-2 font-default" style={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: "hsl(45 90% 45%)"
                }}>
                      <span className="relative">
                        Learn more
                        <span className="absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-300 ease-out group-hover/link:w-full" style={{
                      backgroundColor: "hsl(45 90% 45%)"
                    }} />
                      </span>
                      <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                    </button>
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}